import { useEffect, useState, useCallback } from "react";
import { api, apiError, API, getToken } from "../lib/api";
import { PageHeader } from "../components/Layout";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "../components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, PencilSimple, Trash, FileArrowUp, FileText } from "@phosphor-icons/react";
import { toast } from "sonner";

const IMG_BASE = API.replace(/\/api$/, "");

export default function Sops() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [cats, setCats] = useState([]);
  const [stypes, setStypes] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ scope: "service_type", target: "", title: "", content: "", key_steps: "" });
  const [file, setFile] = useState(null);

  const load = useCallback(() => api.get("/sops").then((r) => setRows(r.data)), []);
  useEffect(() => {
    load();
    api.get("/categories").then((r) => setCats(r.data.map((c) => c.name)));
    api.get("/service-types").then((r) => setStypes(r.data.map((s) => s.name)));
  }, [load]);

  const openNew = () => { setForm({ scope: "service_type", target: "", title: "", content: "", key_steps: "" }); setFile(null); setEditId(null); setOpen(true); };
  const openEdit = (s) => { setForm({ scope: s.scope, target: s.target, title: s.title, content: s.content || "", key_steps: (s.key_steps || []).join("\n") }); setFile(null); setEditId(s.id); setOpen(true); };

  const save = async () => {
    if (!form.target || !form.title) { toast.error("Select target and enter a title"); return; }
    const payload = { ...form, key_steps: form.key_steps.split("\n").map((x) => x.trim()).filter(Boolean) };
    try {
      let sid = editId;
      if (editId) await api.put(`/sops/${editId}`, payload);
      else { const { data } = await api.post("/sops", payload); sid = data.id; }
      if (file && sid) {
        const fd = new FormData(); fd.append("file", file);
        await api.post(`/sops/${sid}/file`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success("SOP saved"); setOpen(false); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const del = async (s) => { if (window.confirm("Delete SOP?")) { await api.delete(`/sops/${s.id}`); load(); } };

  const targetOptions = form.scope === "category" ? cats : stypes;

  return (
    <>
      <PageHeader title="Service SOPs" subtitle="Standard operating procedures partners must follow"
        actions={isAdmin && <Button onClick={openNew} className="rounded-sm gap-2" data-testid="add-sop-btn"><Plus size={16} weight="bold" /> Add SOP</Button>} />
      <div className="p-8 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="sops-grid">
        {rows.length === 0 && <p className="text-sm text-muted-foreground">No SOPs yet.</p>}
        {rows.map((s) => (
          <Card key={s.id} className="rounded-sm p-5 border-border" data-testid={`sop-card-${s.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <h4 className="font-heading font-semibold">{s.title}</h4>
                <p className="text-xs text-muted-foreground mt-0.5"><span className="uppercase tracking-wide">{s.scope === "category" ? "Category" : "Service"}</span> · {s.target}</p>
              </div>
              {isAdmin && (
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(s)} data-testid={`sop-edit-${s.id}`}><PencilSimple size={15} /></Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => del(s)} data-testid={`sop-delete-${s.id}`}><Trash size={15} /></Button>
                </div>
              )}
            </div>
            {s.content && <div className="text-sm text-foreground mt-3 prose prose-sm max-w-none line-clamp-4" dangerouslySetInnerHTML={{ __html: s.content }} />}
            {(s.key_steps && s.key_steps.length > 0) && (
              <div className="mt-3 rounded-sm border border-primary/20 bg-primary/5 p-3" data-testid={`sop-steps-${s.id}`}>
                <p className="label-caps text-primary mb-1.5">Key steps to follow</p>
                <ol className="list-decimal list-inside space-y-1 text-sm text-foreground">
                  {s.key_steps.map((step, i) => <li key={i}>{step}</li>)}
                </ol>
              </div>
            )}
            <div className="flex items-center justify-between mt-3 text-xs text-muted-foreground">
              <span>Updated {(s.updated_at || "").slice(0, 10)}</span>
              {s.has_file && <a href={`${IMG_BASE}${s.file_url}?auth=${getToken()}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary font-semibold" data-testid={`sop-file-${s.id}`}><FileText size={14} /> {s.file_name}</a>}
            </div>
          </Card>
        ))}
      </div>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="sop-form">
          <SheetHeader><SheetTitle className="font-heading">{editId ? "Edit SOP" : "New SOP"}</SheetTitle></SheetHeader>
          <div className="py-5 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="label-caps">Scope</Label>
                <Select value={form.scope} onValueChange={(v) => setForm({ ...form, scope: v, target: "" })}>
                  <SelectTrigger className="rounded-sm mt-1.5" data-testid="sop-scope"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="service_type">Service Type</SelectItem><SelectItem value="category">Category</SelectItem></SelectContent>
                </Select>
              </div>
              <div>
                <Label className="label-caps">Target</Label>
                <Select value={form.target} onValueChange={(v) => setForm({ ...form, target: v })}>
                  <SelectTrigger className="rounded-sm mt-1.5" data-testid="sop-target"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{targetOptions.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label className="label-caps">Title</Label><Input className="rounded-sm mt-1.5" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="sop-title" /></div>
            <div>
              <Label className="label-caps">Instructions (rich text / HTML)</Label>
              <textarea className="w-full rounded-sm border border-input mt-1.5 p-3 text-sm min-h-[160px] font-mono" value={form.content}
                onChange={(e) => setForm({ ...form, content: e.target.value })} placeholder="<ol><li>Step one</li><li>Step two</li></ol>" data-testid="sop-content" />
            </div>
            <div>
              <Label className="label-caps">Key steps to follow (one per line)</Label>
              <p className="text-xs text-muted-foreground mt-0.5">Shown as a quick checklist on the SOP card so partners see the essentials without opening the full document.</p>
              <textarea className="w-full rounded-sm border border-input mt-1.5 p-3 text-sm min-h-[120px]" value={form.key_steps}
                onChange={(e) => setForm({ ...form, key_steps: e.target.value })}
                placeholder={"Carry ID card & wear uniform\nInspect the appliance before starting\nShare final estimate & collect payment\nUpdate job status in the app"} data-testid="sop-key-steps" />
            </div>
            <div>
              <Label className="label-caps">Attach file (PDF/DOC, optional)</Label>
              <label className="flex items-center gap-2 border border-dashed border-input rounded-sm px-3 py-2.5 mt-1.5 cursor-pointer text-sm">
                <FileArrowUp size={16} /> <span>{file ? file.name : "Choose file"}</span>
                <input type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={(e) => setFile(e.target.files?.[0])} data-testid="sop-file-input" />
              </label>
            </div>
          </div>
          <SheetFooter><Button className="rounded-sm" onClick={save} data-testid="sop-save">Save SOP</Button></SheetFooter>
        </SheetContent>
      </Sheet>
    </>
  );
}
